/* ============================================================
   IA Landing Videos — "See it in Action" row coordinator.

   Native <video> cards. Behaviour:
   - autoplay ON: after window 'load', the VISIBLE cards play muted + looped
     (preload flips to 'auto' only then — nothing is fetched before load).
     The play button stays visible (tap = sound).
   - tapping a card -> that card plays "normally": unmuted, native controls,
     no loop; every OTHER card drops back to a muted preview (autoplay on) or
     pauses (autoplay off). Only one card has sound at a time.
   ============================================================ */
if (!customElements.get("ia-lndg-video-row")) {
  customElements.define(
    "ia-lndg-video-row",
    class IaLndgVideoRow extends HTMLElement {
      connectedCallback() {
        this.autoplay = this.getAttribute("autoplay") === "true";
        this.cards = Array.from(this.querySelectorAll("[data-ia-card]"));

        this.cards.forEach((card) => {
          card.addEventListener("click", () => {
            if (!card.__active) this._activate(card);
          });
        });

        if (this.autoplay) this._initAutoplay();
      }

      disconnectedCallback() {
        if (this._io) this._io.disconnect();
      }

      _video(card) {
        return card.querySelector("[data-ia-video]");
      }

      /* User tapped a card → real playback with sound + controls. */
      _activate(card) {
        const video = this._video(card);
        if (!video) return;
        this.cards.forEach((c) => {
          if (c !== card) this._deactivate(c);
        });
        this.interacted = true;
        card.__active = true;
        card.classList.add("is-active");
        video.preload = "auto";
        video.loop = false;
        video.muted = false;
        video.controls = true;
        try { video.currentTime = 0; } catch (e) {} /* play from the beginning */
        video.play().catch(() => {});
      }

      /* Back to muted preview (autoplay + in view) or paused. */
      _deactivate(card) {
        const video = this._video(card);
        if (!video) return;
        card.__active = false;
        card.classList.remove("is-active");
        video.controls = false;
        video.pause(); /* stop the others — no muted preview once the user clicked */
      }

      _inView(card) {
        const r = card.getBoundingClientRect();
        return r.top < window.innerHeight && r.bottom > 0;
      }

      _initAutoplay() {
        const start = () => {
          this._io = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (this.interacted) return; // user took over → stop auto-previewing
                const card = entry.target;
                if (card.__active) return; // never touch the active (sound) card
                const video = this._video(card);
                if (!video) return;
                if (entry.isIntersecting) {
                  video.preload = "auto";
                  video.loop = true;
                  video.muted = true;
                  video.play().catch(() => {});
                } else {
                  video.pause();
                }
              });
            },
            { threshold: 0.4 }
          );
          this.cards.forEach((c) => this._io.observe(c));
        };

        if (document.readyState === "complete") start();
        else window.addEventListener("load", start, { once: true });
      }
    }
  );
}
